import React from "react";
import * as moment from "moment";
import * as _ from "lodash";
import {
    useTable,
    usePagination,
    useSortBy,
    useFilters,
    useGroupBy,
    useExpanded,
    useRowSelect,
} from "react-table";
import { useDrag } from "react-dnd";
import bootbox from "bootbox";


import { ModelApi } from "../api/modelApi";
import { UiUtils } from "../common/uiUtils";
import { ViewerUtils } from "../common/viewerUtils";

const sdHref = (model) => {
    return `/sd/${model.queryId}`;
}

const mapViewsById = (resp) => {
    return resp.data.reduce((o, val) => {
            o[val.id] = val.viewCount;
            return o;
        },
        {});
}

const getQueryIds = (models: any[]): string[] => {
    return _.reject(_.flatten(models).map(m => m.queryId), _.isEmpty);
}

const updateData = (models, views, visitors) => {
    return models.map(m => {
        return {
            ...m,
            viewCount: views[m.queryId] != null ? views[m.queryId] : "-",
            visitorCount: visitors[m.queryId] != null ? visitors[m.queryId] : "-"
        }
    });
}

const ModelRow = (props) => {
    const { row } = props;

    const [{ isDragging }, drag] = useDrag({
        item: { type: "modelRow", model: row.original },
        collect: monitor => ({
            isDragging: !!monitor.isDragging(),
        }),
    });

    return <tr ref={drag} 
        {...row.getRowProps()}>
               {row.cells.map(cell => {
                   return (
                       <td
                           {...cell.getCellProps()}
                           style={{
                        padding: "10px",
                        border: "solid 1px gray",
                    }}>
                           {cell.render("Cell")}
                       </td>
                   );
               })}
           </tr>;
}

const ModelLinkCell = props => {
    const expiration = moment(props.row.original.expirationDate);
    const expired = expiration.isBefore(moment());
    const linkStyle = {
        color: expired ? "grey" : null,
        paddingLeft: props.row.original.revision === 0 ? null : "50px"
    };

    return <a href={sdHref(props.row.original)} target="_blank" style={linkStyle}>{props.value}</a>;
}

const AddRevisionButton = (props) => {
    return <button className="btn btn-primary btn-sm" title="Upload new revision" disabled={props.pending}
                   onClick={() => {
                       const uploadUrl =
                           `${ViewerUtils.sdUrl()}/upload?group=default&projectId=${props.projectModel.project.id
                               }&queryId=${props.row.original.queryId}`;
                       window.location.href = uploadUrl;
                   }}>
                   <span className="icon ion-md-add"></span>
               </button>;
}

const DeleteButton = (props) => {
    return <button className="btn btn-danger btn-sm ml-1" title="Delete model" disabled={props.pending} onClick={() => {
        bootbox.confirm(`Do you really want to delete the model ${props.row.original.name}?`,
            async (result) => {
                if (result) {
                    try {
                        props.setPending(true);
                        await ModelApi.deleteModel("default", props.projectModel.project.id, props.value);
                        UiUtils.notifySuccess("Model deleted.");
                        props.setPending(false);
                    } finally {
                        await props.fetchModels();
                    }
                }
            });
    }}>
               <span className="icon ion-md-trash"></span>
           </button>;
}

const ModelList = (props) => {
    const [models, setModels] = React.useState([]);
    const [views, setViews] = React.useState({});
    const [visitors, setVisitors] = React.useState({});
    const [pending, setPending] = React.useState(false);

    const fetchModels = async () => {
        setPending(true);
        const { data } = await ModelApi.getModels("default", props.projectModel.project.id);
        setModels(data);
        setPending(false);
        return data;
    }

    React.useEffect(() => {
        async function fetch() {
            const ms = await fetchModels();

            if (props.projectModel.authenticated) {
                const idChunks = _.chunk(getQueryIds(ms), 50);
                let allViews = {};
                let allVisitors = {};
                for (let chunk of idChunks) {
                    await ModelApi.getAllTimePageViewReport(chunk).then(resp => {
                        const chunkAnalytics = mapViewsById(resp);
                        allViews = { ...allViews, ...chunkAnalytics };
                        setViews({ ...allViews });
                    });

                    await ModelApi.getAllTimeUniquePageVisitorReport(chunk).then(resp => {
                        const chunkAnalytics = mapViewsById(resp);
                        allVisitors = { ...allVisitors, ...chunkAnalytics };
                        setVisitors({ ...allVisitors });
                    });
                }
            }
        }

        fetch();
    }, []);

    const data = React.useMemo(() => updateData(models, views, visitors), [models, views, visitors]);
    const columns = React.useMemo(() => _.compact([
        {
            Header: "Name",
            accessor: "name",
            Cell: ({ value, row }) => {
                return <ModelLinkCell row={row} value={value}/>;
            }
        },
        {
            Header: "Revision",
            accessor: "revision"
        },
        {
            Header: "Released",
            accessor: "releaseDate",
            Cell: ({ value }) => UiUtils.formatDateTime(value)
        },
        {
            Header: "Expires",
            accessor: "expirationDate",
            Cell: ({ value }) => UiUtils.formatDateTime(value)
        },
        props.projectModel.authenticated
            ? {
                Header: "Views",
                accessor: "viewCount"
            }
            : null,
        props.projectModel.authenticated
            ? {
                Header: "Visitors",
                accessor: "visitorCount"
            }
            : null,
        props.projectModel.authenticated
            ? {
                Header: "",
                accessor: "id",
                Cell: ({ value, row }) =>
                    <div>
                        <AddRevisionButton pending={pending} projectModel={props.projectModel} row={row} />
                        {props.projectModel.hasAdminRole &&
                            <DeleteButton pending={pending} projectModel={props.projectModel} row={row} value={value} setPending={setPending} fetchModels={fetchModels} />
                        }
                    </div>
        }: null
    ]),
        [pending]);


    const pageSizeOptions = [10, 20, 50, "all"];

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex, pageSize },
        gotoPage,
        previousPage,
        nextPage,
        setPageSize,
        canPreviousPage,
        canNextPage,
    } = useTable({ columns, data }, usePagination);

    return (
        <div key="modelList" className="row mb-3">
            <div className="col">
            <div className="row">
                <div className="col">
                    <h5>Models</h5>
                </div>
            </div>
            <div className="row">
            <div className="col">
                <div className="row no-gutters">
                    <table className="table">
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column, index) => (
                                        <th
                                            {...column.getHeaderProps()}
                                            style={{
                                                borderBottom: "solid 3px",
                                                color: "black",
                                                fontWeight: "bold",
                                            }}>
                                            {column.render("Header")}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                                </thead>
                        <tbody {...getTableBodyProps()}>
                                    {pending && <tr><td colSpan={7}><p className="pending"><span>.</span><span>.</span><span>.</span></p></td></tr>}
                                    {page.map(row => {
                                        prepareRow(row);
                                        return (
                                            <ModelRow key={row.original.id} row={row}/>
                                        );
                                    })}
                        </tbody>
                    </table>
                </div>
                <form>
                    <div className="form-row justify-content-start no-gutters">
                        <div className="col-md-auto">
                            <button type="button" className="btn btn-primary" onClick={() => previousPage()} disabled={!canPreviousPage}>
                                {"<"}
                            </button>
                        </div>
                        <div className="col-md-auto">
                            <span style={{ verticalAlign: "middle" }}>
                                {pageIndex + 1} / {pageOptions.length}
                            </span>
                        </div>
                        <div className="col-md-auto">
                            <button type="button" className="btn btn-primary ml-1" onClick={() => nextPage()} disabled={!canNextPage}>
                                {">"}
                            </button>
                        </div>

                        <div className="col-md-auto">
                            <input
                                className="form-control"
                                type="number"
                                placeholder="Go to page"
                                onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }} />
                        </div>
                        <div className="col-md-auto">
                            <select
                                className="form-control"
                                value={pageSize}
                                onChange={e => {
                                    setPageSize(Number(e.target.value));
                                }}>
                                {pageSizeOptions.map(pageSize => (
                                    <option key={pageSize} value={pageSize === "all" ? data.length : pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </form>
            </div>
            </div>
            </div>
            </div>
    );
}

export default ModelList;